import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { showSuccessNotification } from '@/utils/notifications';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateCurrentUserProfileMutation } from '@/hooks/useCurrentUser';

export function ProfileSection() {
  const { t } = useTranslation('settings');
  const { currentUser, refreshCurrentUser } = useAuth();
  const updateProfileMutation = useUpdateCurrentUserProfileMutation();
  const currentFullName = currentUser === null ? '' : currentUser.fullName;
  const [fullName, setFullName] = useState(currentFullName);
  const [errorMessage, setErrorMessage] = useState('');

  const trimmedFullName = fullName.trim();
  const isEmpty = trimmedFullName.length === 0;
  const isUnchanged = trimmedFullName === currentFullName;

  async function submitProfile(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (isEmpty || isUnchanged) {
      return;
    }
    try {
      await updateProfileMutation.mutateAsync({ fullName: trimmedFullName });
      await refreshCurrentUser();
      showSuccessNotification(t('practitioner.profile.saved'));
    } catch (updateError) {
      const backendMessage = resolveApiErrorMessage(updateError);
      setErrorMessage(
        backendMessage.length > 0 ? backendMessage : t('practitioner.profile.saveError')
      );
    }
  }

  return (
    <Card padding="lg">
      <h2 className="text-lg font-bold text-zooindigo">{t('practitioner.profile.heading')}</h2>
      <form onSubmit={submitProfile} className="mt-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="profile-full-name">{t('practitioner.profile.fullName')}</Label>
          <Input
            id="profile-full-name"
            value={fullName}
            onChange={function updateFullName(changeEvent) {
              setFullName(changeEvent.target.value);
            }}
          />
        </div>

        {errorMessage.length > 0 ? (
          <div
            role="alert"
            className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
          >
            <Icon name="AlertCircle" />
            {errorMessage}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={isEmpty || isUnchanged}
          isLoading={updateProfileMutation.isPending}
        >
          {t('practitioner.profile.save')}
        </Button>
      </form>
    </Card>
  );
}
